<template>
    <v-card class="mb-5 pa-2">
        <card-header
            :title="
                $t('roles.form.texts.title', {
                    'title-prefix': titlePrefix,
                })
            "
        />

        <template v-if="!permissions">
            <v-container fluid class="fill-height">
                <v-row justify="center" align="center">
                    <v-col class="text-center" xs="12" md="12">
                        <v-progress-circular
                            :size="50"
                            color="primary"
                            indeterminate
                        ></v-progress-circular>
                    </v-col>

                    <v-col class="text-center">
                        {{ $t('roles.form.texts.loading') }}
                    </v-col>
                </v-row>
            </v-container>
        </template>

        <v-form @submit.prevent="save" v-else>
            <v-card-text>
                <v-row>
                    <v-col
                        cols="12"
                        xs="12"
                        sm="12"
                        md="12"
                        class="grey lighten-4 py-5"
                    >
                        <p class="subtitle-2">
                            <v-icon left>mdi-shield-account</v-icon>
                            {{ $t('roles.breadcrumbs.index') }}
                        </p>
                        <v-text-field
                            flat
                            v-model="role.name"
                            :label="$t('roles.form.labels.name')"
                            :counter="255"
                            :error-messages="nameErrors"
                            @input="$v.role.name.$touch()"
                            @blur="$v.role.name.$touch()"
                            autofocus
                        ></v-text-field>
                        <v-switch
                            v-model="role.account_manager"
                            :label="
                                $t('resources.role.attributes.account_manager')
                            "
                            :false-value="false"
                        >
                        </v-switch>
                    </v-col>
                    <v-col cols="12" xs="12" sm="12" md="12" class="pt-5">
                        <p class="subtitle-2">
                            <v-icon left>mdi-shield-edit</v-icon>
                            {{ $t('roles.form.labels.permissions') }}
                        </p>
                        <v-simple-table>
                            <template v-slot:default>
                                <tbody>
                                    <tr
                                        v-for="(grouped, index) in permissions"
                                        :key="index"
                                    >
                                        <td>
                                            <span class="subtitle-2">{{
                                                $t(
                                                    'permissions.titles.' +
                                                        index,
                                                )
                                            }}</span>
                                        </td>
                                        <td>
                                            <v-row>
                                                <v-col
                                                    cols="2"
                                                    v-for="(item,
                                                    i) in visiblePermissions(
                                                        grouped,
                                                    )"
                                                    :key="i"
                                                >
                                                    <v-switch
                                                        v-model="item.selected"
                                                        :label="
                                                            $t(
                                                                'permissions.names.' +
                                                                    $lodash.first(
                                                                        item.name.split(
                                                                            ' ',
                                                                        ),
                                                                    ),
                                                            )
                                                        "
                                                        class="mx-5"
                                                        @change="
                                                            handlePermission(
                                                                item,
                                                            )
                                                        "
                                                    ></v-switch>
                                                </v-col>
                                            </v-row>
                                        </td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-col>
                </v-row>
            </v-card-text>
            <form-actions :loadingSave="loading"></form-actions>
        </v-form>
    </v-card>
</template>

<script>
import Permission from '@/models/Permission'
import CardHeader from '@/components/utils/CardHeader'
import FormActions from '@/components/utils/FormActions'
import { validationMixin } from 'vuelidate'
import { required, maxLength } from 'vuelidate/lib/validators'

import BaseModel from '@/models/BaseModel'

export default {
    props: {
        model: {
            type: Object,
            required: true,
            validator: model => {
                return model instanceof BaseModel
            },
        },
        isEdit: Boolean,
        titlePrefix: String,
    },

    data: () => {
        return {
            loading: false,
            permissions: null,
            showHiddenPermissions: false,
        }
    },

    components: {
        'card-header': CardHeader,
        'form-actions': FormActions,
    },

    mixins: [validationMixin],

    validations: {
        role: {
            name: { required, maxLength: maxLength(255) },
        },
    },

    methods: {
        async save() {
            this.loading = true

            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('form.messages.validation-error'),
                )

                this.loading = false
                return
            }

            let clone = this.role.clone()
            let res = null

            try {
                res = await clone.save().catch(() => {})
            } finally {
                this.loading = false
            }

            if (!res) return

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('roles.form.bus.save-success', {
                    role: this.role.name,
                }),
            )

            this.$emit('saved')
        },

        async getPermissions() {
            let res = await this.permissionModel.all()

            if (!res) return

            this.permissions = this.parsePermissions(res.data)
        },

        parsePermissions(permissions) {
            let permissionsParsed = this.$lodash.reduce(
                permissions,
                (result, each) => {
                    each.selected = false

                    let hasSelectedPermission = this.$lodash.find(
                        this.model.permissions,
                        item => {
                            return each.id == item.id
                        },
                    )

                    if (hasSelectedPermission) {
                        each.selected = true
                    }

                    let permissionName = this.$lodash.last(each.name.split(' '))

                    if (!this.$lodash.has(result, permissionName)) {
                        result[permissionName] = new Array()
                    }

                    result[permissionName].push(each)

                    return result
                },
                {},
            )

            return permissionsParsed
        },

        visiblePermissions(grouped) {
            if (this.showHiddenPermissions) {
                return grouped
            }

            return this.$lodash.filter(grouped, item => !item.hidden)
        },

        handlePermission(permission) {
            if (typeof this.role.permissions == 'undefined') {
                this.role.permissions = []
            }

            if (permission.selected) {
                this.role.permissions.push(permission)
            } else {
                let index = this.$lodash.findIndex(
                    this.role.permissions,
                    each => {
                        return each.id == permission.id
                    },
                )

                this.role.permissions.splice(index, 1)
            }
        },

        showAllPermissions() {
            let authAdmin = this.$lodash.some(
                this.$store.getters['auth/user'].roles,
                ['name', 'Admin'],
            )

            if (!authAdmin) {
                console.warn(
                    'Somente usuários Admin podem exibir permissões ocultas.',
                )
                return
            }

            this.showHiddenPermissions = true

            let hiddenCount = this.$lodash.sumBy(
                this.$lodash.flatten(this.$lodash.values(this.permissions)),
                item => (item.hidden ? 1 : 0),
            )

            console.log(
                `Permissões ocultas exibidas (${hiddenCount} permissão(ões)).`,
            )
        },
    },

    computed: {
        role() {
            return this.model
        },

        permissionModel() {
            return new Permission()
        },

        nameErrors() {
            const errors = []
            if (!this.$v.role.name.$dirty) return errors

            !this.$v.role.name.required &&
                errors.push(this.$t('roles.form.errors.name-required'))

            !this.$v.role.name.maxLength &&
                errors.push(this.$t('roles.form.errors.name-max-length'))

            return errors
        },
    },

    created() {
        this.getPermissions()
    },

    mounted() {
        window.__resaleShowHiddenPermissions = () => this.showAllPermissions()
    },

    beforeDestroy() {
        delete window.__resaleShowHiddenPermissions
    },
}
</script>
